import * as THREE from 'three';

/**
 * Registering the DOM tap grid with the perspective 3D board.
 *
 * The tap overlay is a flat grid that fills its container, but the camera
 * looks at the board at an angle, so the board is a smaller trapezoid and a
 * flat grid cannot sit over it. The board plane projects to the screen by a
 * homography (a perspective map of a plane to a plane), and so does the
 * overlay-to-plane step, so the whole mapping is one homography fixed by four
 * point pairs. Handing the browser that homography as a CSS `matrix3d` warps
 * the grid onto the board exactly: every cell's centre lands on its square's
 * projected centre, and hit-testing follows the warp.
 */

export type Pt = readonly [number, number];

/** World (x, z) of the four board corners that the overlay's TL, TR, BR, BL
 *  corners correspond to, in that order. */
export interface PlaneCorners {
  readonly tl: Pt;
  readonly tr: Pt;
  readonly br: Pt;
  readonly bl: Pt;
}

/** The chess board's playable area: a-file is +x, rank 8 is +z (see `squareToVec3`). */
export const CHESS_PLANE: PlaneCorners = {
  tl: [4, 4], // a8 outer corner
  tr: [-4, 4], // h8
  br: [-4, -4], // h1
  bl: [4, -4], // a1
};

const v = new THREE.Vector3();

/** World point → pixel inside a canvas of `size`, origin top-left. */
export function worldToPx(
  camera: THREE.Camera,
  size: { width: number; height: number },
  x: number,
  y: number,
  z: number,
): Pt {
  v.set(x, y, z).project(camera);
  return [((v.x + 1) / 2) * size.width, ((1 - v.y) / 2) * size.height];
}

/**
 * Homography h (h11 h12 h13 h21 h22 h23 h31 h32, with h33 = 1) taking each
 * `src[i]` to `dst[i]`: x' = (h11 x + h12 y + h13) / (h31 x + h32 y + 1), and
 * likewise for y'. Solved as the 8x8 linear system by Gaussian elimination.
 */
export function solveHomography(src: readonly Pt[], dst: readonly Pt[]): number[] {
  const a: number[][] = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = src[i] as Pt;
    const [u, w] = dst[i] as Pt;
    a.push([x, y, 1, 0, 0, 0, -u * x, -u * y, u]);
    a.push([0, 0, 0, x, y, 1, -w * x, -w * y, w]);
  }
  for (let col = 0; col < 8; col++) {
    let pivot = col;
    for (let r = col + 1; r < 8; r++) {
      if (Math.abs((a[r] as number[])[col] as number) > Math.abs((a[pivot] as number[])[col] as number)) pivot = r;
    }
    [a[col], a[pivot]] = [a[pivot] as number[], a[col] as number[]];
    const pr = a[col] as number[];
    const div = pr[col] as number;
    for (let c = col; c < 9; c++) pr[c] = (pr[c] as number) / div;
    for (let r = 0; r < 8; r++) {
      if (r === col) continue;
      const row = a[r] as number[];
      const f = row[col] as number;
      if (f === 0) continue;
      for (let c = col; c < 9; c++) row[c] = (row[c] as number) - f * (pr[c] as number);
    }
  }
  return a.map((row) => row[8] as number);
}

/** Apply a homography from `solveHomography` to a point. */
export function applyHomography(h: readonly number[], x: number, y: number): Pt {
  const [h11, h12, h13, h21, h22, h23, h31, h32] = h as [number, number, number, number, number, number, number, number];
  const d = h31 * x + h32 * y + 1;
  return [(h11 * x + h12 * y + h13) / d, (h21 * x + h22 * y + h23) / d];
}

/** CSS `matrix3d(...)` for a 2D homography, to be used with `transform-origin: 0 0`. */
export function homographyToMatrix3d(h: readonly number[]): string {
  const [h11, h12, h13, h21, h22, h23, h31, h32] = h as [number, number, number, number, number, number, number, number];
  const f = (n: number): string => (Math.abs(n) < 1e-9 ? '0' : n.toFixed(7));
  return `matrix3d(${[h11, h21, 0, h31, h12, h22, 0, h32, 0, 0, 1, 0, h13, h23, 0, 1].map(f).join(',')})`;
}

/** The homography taking the overlay's own pixel rectangle (0,0)-(W,H) onto the
 *  board plane as the camera sees it. */
export function overlayHomography(
  camera: THREE.Camera,
  size: { width: number; height: number },
  plane: PlaneCorners,
): number[] {
  const { width: W, height: H } = size;
  const src: Pt[] = [
    [0, 0],
    [W, 0],
    [W, H],
    [0, H],
  ];
  const dst = [plane.tl, plane.tr, plane.br, plane.bl].map(([x, z]) => worldToPx(camera, size, x, 0, z));
  return solveHomography(src, dst);
}

/** `matrix3d` string for the overlay, or null while the canvas has no size yet. */
export function overlayMatrix3d(
  camera: THREE.Camera,
  size: { width: number; height: number },
  plane: PlaneCorners,
): string | null {
  if (size.width <= 0 || size.height <= 0) return null;
  return homographyToMatrix3d(overlayHomography(camera, size, plane));
}
